import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * The console's own dropdown, in the console's colours instead of the
 * browser's native list (which ignores the dark theme on Windows). A button
 * opens a panel of options with a check on the chosen one; mouse and keyboard
 * (arrows, Enter, Escape, Tab) both work, and a click outside closes it.
 */
export function Select({
  value,
  onChange,
  options,
  label,
  disabled,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  /** Accessible name of the control. */
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function openPanel() {
    if (disabled) return;
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  }

  function choose(index: number) {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent) {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openPanel();
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(active);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`} onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-slate-950 px-3 py-2 text-left text-sm text-slate-100 transition focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
          open
            ? 'border-violet-500 ring-1 ring-violet-500/40'
            : 'border-slate-700 hover:border-slate-600'
        }`}
      >
        <span className="truncate">{selected?.label ?? '—'}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute right-0 z-30 mt-1.5 max-h-72 min-w-full overflow-auto rounded-lg border border-slate-700 bg-slate-900 p-1 shadow-xl shadow-black/40"
        >
          {options.map((o, i) => {
            const isSelected = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(i)}
                className={`flex cursor-pointer items-center justify-between gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm ${
                  i === active ? 'bg-violet-500/15 text-white' : 'text-slate-200'
                }`}
              >
                <span>
                  <span className="block">{o.label}</span>
                  {o.description && (
                    <span className="block text-xs text-slate-400">{o.description}</span>
                  )}
                </span>
                {isSelected && <Check className="h-4 w-4 shrink-0 text-violet-300" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
